import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { map, catchError, tap } from 'rxjs/operators';
import { Observable, BehaviorSubject } from 'rxjs';
import { environment } from '@environment/environment';
import { Utilisateur} from '@app/models';
import { JwtHelperService } from '@auth0/angular-jwt';
import { Router, ActivatedRoute } from '@angular/router';
import { DataService, LocalService, UtilisateurService } from '@app/services';
import { Alert } from '@app/models';
import Swal from 'sweetalert2';
import { TranslateService } from '@ngx-translate/core';

@Injectable()
export class AuthenticationService {
    public currentUserValue: BehaviorSubject<any>;
    public currentLangueValue: BehaviorSubject<any>;
    toast = new Alert();

    returnUrl: string;

    constructor(
        private route: ActivatedRoute,
        private localService: LocalService,
        private dataService: DataService,
        public translate: TranslateService,
        private utilisateurService: UtilisateurService,
        private router: Router,
        private httpClient: HttpClient,
        public jwtHelper: JwtHelperService
    ) {
        this.returnUrl = this.route.snapshot.queryParams['`${returnUrl}`'] || '/';

        this.currentUserValue = new BehaviorSubject<any>(null);
        this.currentLangueValue = new BehaviorSubject<any>(null);

        // init utilisateur
        if (this.localService?.getJsonValue('currentUtilisateur')) {
            this.currentUserValue.next(this.localService?.getJsonValue('currentUtilisateur'));
        }

        // init langue
        if (this.localService?.getJsonValue('currentLangue')) {
            this.currentLangueValue.next(this.localService?.getJsonValue('currentLangue'));
        }
    }

    // decodage du token serveur
    public decodeToken(token: string) {
        if(token){
            return this.jwtHelper.decodeToken(token);
        }
        return null;
    }

    // TOKEN: lecture seule du token serveur
    get appToken() {
        if (this.localService.getJsonValue('token')) {
            //lire le token sauvegardé
            return this.localService.getJsonValue('token');
        }
        return null;
    }

    // USER: lecture seule de l'utilisateur courant
    get appUser(): any {
        if (this.currentUserValue) {
            return this.currentUserValue.value;
        }
        return null;
    }

    // sauvegarde du token serveur
    public saveAppToken(token: string): void {
        this.localService.removeJsonValue('token');

        if(!this.localService.getJsonValue('token')){
            //sauvegarde du token dans le localstorage
            this.localService.setJsonValue('token', token);
        }
    }

    // sauvegarde de l'utilisateur courant
    public saveAppUser(user: any): void {
        this.localService.removeJsonValue('currentUtilisateur');

        if(!this.localService.getJsonValue('currentUtilisateur')){
            //sauvegarde de l'utilisateur dans le localstorage
            this.localService.setJsonValue('currentUtilisateur', user);

            //pub
            this.currentUserValue.next(this.localService.getJsonValue('currentUtilisateur'));
        }
    }

    // sauvegarde de la langue
    public saveAppLanguage(langue: string): void {
        this.localService.removeJsonValue('currentLangue');

        if(!this.localService.getJsonValue('currentLangue')){
            //sauvegarde de la langue dans le localstorage
            this.localService.setJsonValue('currentLangue', langue);

            //pub
            this.currentLangueValue.next(this.localService.getJsonValue('currentLangue'));
        }
    }

    // Lecture des nouvelles infos User en bdd
    public reloadUser(userId: any): any {
        let refresh = this.utilisateurService.getUtilisateurById(userId) as Observable<any>;
        if(refresh){
            refresh.subscribe(
                (user) => {
                    if (user) {
                        if(this.isAllowed(user.status_id) == false) this.logout();
                        //sauvegarde de l'utilisateur
                        this.saveAppUser(user);
                    }
                },
                (error) => {
                    console.error(error);
                    if (error) {
                        this.logout();
                    }
                });
        }

        return refresh;
    }

    // Redirection vers page indiquée
    public redirectByRole(page: string, alert = false, roleId: number = null){
        let destpage: string = '';
        
        //accès refusé
        if(alert){
            Swal.fire({...this.toast, imageUrl: 'assets/images/lock-access.jpg', imageWidth: 100, title: this.translate.instant('Forbidden'), html: this.translate.instant('you don\'t have permission to access this page.')});
        }

        //accès refusé
        if(this.hasBadRole(roleId)){
            //destruction connexion
            this.resetcredentials();
            Swal.fire({...this.toast, imageUrl: 'assets/images/lock-access.jpg', imageWidth: 100, title: this.translate.instant('Forbidden'), html: this.translate.instant('you don\'t have permission to access this page.')});
            return false; // Désactiver l'itinéraire
        }

        switch(roleId){
            case 1:
                destpage = '/admin/table-domaines';
                break;
            case 2:
                destpage = '/domains/sites';
                break;
            case 3:
                destpage = '/domains/mysites';
                break;
            case 4:
                destpage = '/admin/table-prospection';
                break;
            default:
                destpage = '';
                break;
        }

        page = page?? destpage;

        this.router.navigateByUrl(page);
        return true; // Activer l'itinéraire
    }

    // check Statut de l'utilisateur
    isAllowed(status) {
        return this.isLoggedIn() && (status == 2 || status == 3);
    }

    // utilisateur Admin désigné Support
    isSupport() {
        return this.isAdmin() && this.appUser?.id == environment.admin;
    }

    // mauvais role
    hasBadRole(role_id) {
        return role_id != null && (role_id < 1 || role_id > 4);
    }

    // aucun role
    hasNoRole() {
        return !this.isLoggedIn() || (this.appUser?.role_id < 1 || this.appUser?.role_id > 4);
    }

    // role Admin
    isAdmin() {
        return this.isLoggedIn() && this.appUser?.role_id == 1;
    }

    // role Dealer
    isDealer() {
        return this.isLoggedIn() && this.appUser?.role_id == 2;
    }

    // role User
    isUser() {
        return this.isLoggedIn() && this.appUser?.role_id == 3;
    }

    // role Prospecteur
    isProspector() {
        return this.isLoggedIn() && this.appUser?.role_id == 4;
    }
    
    // verifie si session existe et si token expiré ou non
    isLoggedIn() {
        return this.appUser && !this.jwtHelper.isTokenExpired(this.appToken);
    }

    //clear all stored data
    resetcredentials() {
        if(this.currentUserValue) this.currentUserValue.next(null);
        if(this.currentLangueValue) this.currentLangueValue.next(null);
        this.localService.clearToken(); //supprimer plutot que le token en localstorage
    }

    // connexion classique
    login(object: Utilisateur) {
        return this.httpClient
            .post<any>(`${environment.apiEndPoint}login`, object)
            .pipe(
                tap(res => {
                    // login successful if there's a jwt token in the response
                    if (res?.token) {
                        this.resetcredentials();

                        //sauvegarde du token
                        this.saveAppToken(res.token);

                        //decodage du token
                        let currentUser = this.decodeToken(res.token)

                        //sauvegarde de l'utilisateur
                        this.saveAppUser(currentUser);

                        // Injection de la langue depuis page login
                        this.saveAppLanguage(object.langue);

                    }
                }),
                map(res => this.decodeToken(res?.token)),
                catchError(this.handleError)
            );
    }

    // deconnexion
    logout() {
        // clear all stored data
        this.resetcredentials();

        // verification des infos utilisateurs existantes
        this.dataService.changeState(false);

        // Renvoi vers page login
        this.redirectByRole('/auth/login', false, null);
    }

    // deconnexion et affichage page de maintenace
    maintenance() {
        // clear all stored data
        this.resetcredentials();

        // Renvoi vers page login
        this.router.navigateByUrl('/auth/login');
        this.dataService.changeState(false);

        // diffusion du probleme de BDD
        this.dataService.changeMessage('database', true);
    }

    // enregistrement
    signup(object: Utilisateur) {
        return this.httpClient
            .post<any>(`${environment.apiEndPoint}signup`,object)
            .pipe(
                map(res => res),
                catchError(this.handleError)
            );
    }

    // confirmer l'email du nouvel utilisaeur
    confirmEmail(object) {
        return this.httpClient
            .post<any>(`${environment.apiEndPoint}confirm`, object)
            .pipe(
                map(res => res),
                catchError(this.handleError)
            );
    }

    // demande d'un nouveau mot de passe
    changePasswordToken(object) {
        return this.httpClient
            .post<any>(
                `${environment.apiEndPoint}recovery`, object)
            .pipe(
                map(res => res),
                catchError(this.handleError)
            );
    }

    // changer mot de passe perdu
    resetPassword(email: string) {
        return this.httpClient
            .post<any>(
                `${environment.apiEndPoint}reset`,
                JSON.stringify({ email: `${email}` })
            )
            .pipe(
                map(res => res),
                catchError(this.handleError)
            );
    }

    // exception levee
    private handleError(error: any): Promise<any> {
        let retour = error?.error?.message || error?.message || error;
        if(error?.error?.data) retour += '<br />'+error?.error?.data;
        return Promise.reject(retour);
    }
}
